<template>
  <vs-popup title="ชำระผ่าน QR CODE" class="wel-payment-qr-modal" :active.sync="popupActive">
    <div v-if="qr" class="payment-qr-detail">
      <div v-for="row in breakdown" :key="row.field" class="payment-qr-row">
        <span>{{ row.label }}</span><strong>{{ money(row.amount) }}</strong>
      </div>
      <div class="payment-qr-row payment-qr-total">
        <span>ยอดรวม</span><strong><span class="payment-qr-amount">{{ money(qr.amount) }}</span> บาท</strong>
      </div>
      <p class="payment-qr-due">ชำระภายในวันที่ {{ qr.dueDate || 'ไม่พบข้อมูลกำหนดชำระ' }}</p>
      <div ref="qrImage" class="payment-qr-image"></div>
      <p v-if="qrError" role="alert" class="text-danger">{{ qrError }}</p>
      <p>ใช้แอปพลิเคชันธนาคารสแกน QR CODE นี้ เพื่อชำระเงินรองรับทุกธนาคาร โดยไม่มีค่าธรรมเนียม</p>
      <p v-if="saveError" role="alert" class="text-danger">{{ saveError }}</p>
      <vs-button class="mt-4" :disabled="!qrDataUrl || saving || !!qrError" @click="saveJpg">{{ saving ? 'กำลังบันทึก…' : 'บันทึก QR Code เป็น JPG' }}</vs-button>
    </div>
  </vs-popup>
</template>
<script>
import * as QRCode from 'easyqrcodejs'
import qrLogo from '@/assets/images/logo/logo-wel-barcode.png'
export default {
  name: 'QrPaymentModal',
  props: { active: { type: Boolean, default: false }, qr: { type: Object, default: null } },
  data () { return { qrError: '', qrInstance: null, renderSequence: 0, qrDataUrl: '', saving: false, saveError: '' } },
  computed: {
    popupActive: {
      get () { return this.active },
      set (value) { this.$emit('update:active', value) }
    },
    breakdown () {
      if (!this.qr) return []
      return [
        ['paidFee', 'ค่าธรรมเนียม'], ['paidPenalty', 'เบี้ยปรับ'],
        ['paidInterest', 'ดอกเบี้ย'], ['paidPrinciple', 'เงินต้น'],
        ['paidContractFee', 'ค่าธรรมเนียมการทำสัญญา'], ['paidTaxAmount', 'ค่าอากรแสตมป์'],
        ['paidProcessingFee', 'ค่าดำเนินการ'], ['paidCourtOrderExpense', 'ค่าใช้จ่ายอื่นตามคำสั่งศาล'],
        ['balanceRemain', 'ดอกเบี้ยรับล่วงหน้า']
      ].filter(([field]) => this.qr[field] > 0).map(([field, label]) => ({ field, label, amount: this.qr[field] }))
    }
  },
  watch: {
    active () { this.renderQr() },
    qr: { deep: true, handler () { this.renderQr() } }
  },
  mounted () { this.renderQr() },
  beforeDestroy () { this.renderSequence++; this.clearQr() },
  methods: {
    money (value) { return Number(value).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    clearQr () {
      this.qrDataUrl = ''
      this.saveError = ''
      if (this.qrInstance) this.qrInstance.clear()
      this.qrInstance = null
      if (this.$refs.qrImage) this.$refs.qrImage.innerHTML = ''
    },
    async renderQr () {
      const sequence = ++this.renderSequence
      this.clearQr()
      this.qrError = ''
      await this.$nextTick()
      if (sequence !== this.renderSequence || !this.active || !this.qr || !this.$refs.qrImage) return
      const qr = this.qr
      if (['extra', 'registrationFeeExtra'].includes(qr.type) || !qr.ref1 || !qr.ref2 || !qr.taxId || !qr.suffix || !Number.isFinite(qr.amount) || !(qr.amount > 0)) {
        this.qrError = 'ข้อมูล QR ไม่ครบถ้วนหรือประเภทนี้ไม่รองรับการแสดง QR'
        return
      }
      const element = this.$refs.qrImage
      // A fresh detached-on-close host isolates delayed logo/image callbacks from the next QR.
      const host = element.ownerDocument.createElement('div')
      element.appendChild(host)
      const text = '|' + qr.taxId + qr.suffix + '\r' + qr.ref1 + '\r' + qr.ref2 + '\r' + qr.amount.toFixed(2).replace(/\./g, '')
      try {
        this.qrInstance = new QRCode(host, {
          text, width: 256, height: 256, colorDark: '#000000', colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.M, logo: qrLogo, logoWidth: 40, logoHeight: 40, logoBgTransparent: true,
          onRenderingEnd: (options, dataUrl) => {
            if (sequence === this.renderSequence && this.active && typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')) this.qrDataUrl = dataUrl
          }
        })
      } catch (_) { this.clearQr(); this.qrError = 'แสดง QR ไม่สำเร็จ กรุณาลองเปิดใหม่' }
    },
    async saveJpg () {
      if (!this.active || !this.qr || !this.qrDataUrl || this.qrError || this.saving) return
      this.saving = true
      this.saveError = ''
      const sequence = this.renderSequence
      const qr = { ...this.qr }
      const rows = this.breakdown.map(row => ({ ...row }))
      const dataUrl = this.qrDataUrl
      try {
        const image = new Image()
        await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = reject; image.src = dataUrl })
        if (sequence !== this.renderSequence || !this.active) return
        const canvas = document.createElement('canvas')
        canvas.width = 800
        const detailsHeight = rows.length * 44
        canvas.height = 1000 + detailsHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('Canvas unavailable')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.textAlign = 'center'
        ctx.fillStyle = '#0f4c81'
        ctx.font = 'bold 32px sans-serif'
        ctx.fillText('ชำระผ่าน QR CODE', 400, 65, 720)
        ctx.fillStyle = '#000000'
        ctx.font = '24px sans-serif'
        ctx.fillText('เลขที่สัญญา ' + qr.ref1, 400, 115, 720)
        ctx.font = '24px sans-serif'
        rows.forEach((row, index) => {
          const y = 165 + index * 44
          ctx.textAlign = 'left'
          ctx.fillText(row.label, 40, y, 470)
          ctx.textAlign = 'right'
          ctx.fillText(this.money(row.amount), 760, y, 220)
        })
        ctx.textAlign = 'center'
        ctx.font = 'bold 36px sans-serif'
        ctx.fillText('ยอดรวม ' + this.money(qr.amount) + ' บาท', 400, 173 + detailsHeight, 720)
        // Preserve square modules and leave a white quiet zone around the QR.
        ctx.imageSmoothingEnabled = false
        ctx.drawImage(image, 144, 240 + detailsHeight, 512, 512)
        ctx.font = '24px sans-serif'
        ctx.fillText('ชำระภายในวันที่ ' + (qr.dueDate || 'ไม่พบข้อมูลกำหนดชำระ'), 400, 820 + detailsHeight, 720)
        ctx.font = '20px sans-serif'
        ctx.fillText('เลขอ้างอิง ' + qr.ref2, 400, 870 + detailsHeight, 720)
        ctx.fillText('โปรดตรวจสอบชื่อผู้รับและยอดเงินก่อนยืนยันชำระ', 400, 930 + detailsHeight, 720)
        const safe = value => String(value).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80)
        const link = document.createElement('a')
        link.download = `QR-${safe(qr.ref1)}-${safe(qr.ref2)}.jpg`
        link.href = canvas.toDataURL('image/jpeg', 1)
        document.body.appendChild(link)
        try { link.click() } finally { link.remove() }
      } catch (_) {
        if (sequence === this.renderSequence && this.active) this.saveError = 'บันทึกภาพไม่สำเร็จ กรุณาลองใหม่หรือเปิด QR อีกครั้ง'
      } finally { this.saving = false }
    }
  }
}
</script>
<style scoped>
.payment-qr-detail { color: #8389a2; font-size: 15px; line-height: 1.6; }
.payment-qr-row { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 8px; }
.payment-qr-row strong { color: #000; text-align: right; white-space: nowrap; }
.payment-qr-total { margin-top: 32px; margin-bottom: 20px; }
.payment-qr-amount { color: #0f4c81; font-size: 23px; }
.payment-qr-due { color: #000; font-weight: 600; margin-bottom: 8px; }
.payment-qr-image { display: flex; justify-content: center; margin-bottom: 8px; }
.payment-qr-image >>> div { max-width: 100%; min-width: 0; }
.payment-qr-image >>> canvas, .payment-qr-image >>> img { max-width: 100%; height: auto; }
</style>
<style>
.wel-payment-qr-modal.con-vs-popup .vs-popup { width: 400px !important; max-width: calc(100vw - 32px); height: auto !important; }
</style>
